// =============================================================================
// VandieZ admin-web -- Manual bank-account verification modal (defect DR-24)
// =============================================================================
// Shared by two surfaces:
//   * /users/:userId          — the bank-accounts card, per-row "Review"
//   * /bank-accounts/pending  — the ops work queue
//
// There is no automated verifier. An admin opening this modal is
// ATTESTING that they compared the holder name, IFSC and last-4 against
// a document, and that attestation (who / when / on what basis) is
// written to audit.bank_account_verifications server-side. The copy
// below is deliberately blunt about that — a payout can only leave the
// platform once somebody has personally vouched for the destination.
//
// Hand-rolled fixed overlay + <Card>: the codebase has no Dialog
// primitive (see InitiatePayoutModal in PayoutsPage.tsx, same shape).
// =============================================================================

import { AlertCircle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";

import { bankAccountsAdminApi } from "@/api/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import {
  BANK_VERIFICATION_BASIS_LABELS,
  type AdminUserBankAccountRow,
  type BankVerificationBasis,
  type BankVerificationStatus,
} from "@/types/admin_user_banks";

// ---------------------------------------------------------------------------
// Status pill — 4 states. Kept here so both surfaces render it identically.
// ---------------------------------------------------------------------------
const STATUS_STYLES: Record<BankVerificationStatus, string> = {
  unverified: "bg-muted text-muted-foreground",
  pending: "bg-warning-subtle text-warning-subtle-foreground",
  verified: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-danger-subtle text-danger-subtle-foreground",
};

const STATUS_LABEL: Record<BankVerificationStatus, string> = {
  unverified: "Unverified",
  pending: "Pending review",
  verified: "Verified",
  rejected: "Rejected",
};

export function BankVerificationStatusPill({
  status,
}: {
  status: BankVerificationStatus;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        STATUS_STYLES[status],
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

const BASIS_OPTIONS = Object.keys(
  BANK_VERIFICATION_BASIS_LABELS,
) as BankVerificationBasis[];

/// Translate a caught error into the message shown in the modal's Alert.
/// Mirrors the ZonesPage friendlyError pattern: name the codes this
/// surface can actually hit, fall back to the RFC-7807 detail.
function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "BANK_ACCOUNT_ALREADY_IN_STATE") {
      return "Someone already ruled on this account — reload to see the current decision.";
    }
    if (e.status === 403) {
      return "You don't have the bank_account.verify permission. Ask a super admin.";
    }
    if (e.status === 404) {
      return "This account no longer exists. It may have been removed by the user.";
    }
    return e.problem.detail;
  }
  return "Unexpected error.";
}

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

interface BankVerificationModalProps {
  /// Owner of the account — the POST route is user-scoped.
  userId: string;
  account: AdminUserBankAccountRow;
  onClose: () => void;
  /// Fired after a successful decision so the caller can refetch its list.
  onDecided: (updated: AdminUserBankAccountRow) => void;
}

export function BankVerificationModal({
  userId,
  account,
  onClose,
  onDecided,
}: BankVerificationModalProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [basis, setBasis] = useState<BankVerificationBasis>("cancelled_cheque");
  const [notes, setNotes] = useState<string>("");
  // Which button is mid-flight — also drives the "…" label on that button
  // only, so the admin can see which decision is being recorded.
  const [submitting, setSubmitting] = useState<"verified" | "rejected" | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Escape closes, matching every other dismissible surface in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const notesFilled = notes.trim().length > 0;
  // Mirror of the backend's model_validator: 'other' needs an explanation
  // or the audit trail is useless, and a rejection always needs one
  // because the user will ask why. Enforced client-side so the admin
  // learns about it before the round trip, not after a 400.
  const verifyBlocked = basis === "other" && !notesFilled;
  const rejectBlocked = !notesFilled;
  const notesRequired = basis === "other";

  const submit = async (decision: "verified" | "rejected"): Promise<void> => {
    if (accessToken === null) {
      return;
    }
    setSubmitting(decision);
    setError(null);
    try {
      const updated = await bankAccountsAdminApi.verify(
        accessToken,
        userId,
        account.id,
        {
          decision,
          basis,
          ...(notesFilled ? { notes: notes.trim() } : {}),
        },
      );
      toast.success(
        decision === "verified"
          ? `Marked ${account.bank_name} ••••${account.account_number_last_4} verified.`
          : `Rejected ${account.bank_name} ••••${account.account_number_last_4}.`,
      );
      onDecided(updated);
      onClose();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(null);
    }
  };

  const busy = submitting !== null;
  const hasPriorDecision =
    account.verified_at !== null || account.verification_basis !== null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bank-verify-title"
    >
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle id="bank-verify-title">Verify bank account</CardTitle>
          <CardDescription>
            Confirm the holder name, IFSC and last-4 below match the
            document in front of you before approving. You are attesting to
            this check — your name is recorded against the decision.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Details to check against the document. */}
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{account.bank_name}</span>
              <div className="flex items-center gap-1">
                {account.is_primary && (
                  <span className="rounded-full bg-info-subtle px-2 py-0.5 text-xs text-info-subtle-foreground">
                    Primary
                  </span>
                )}
                <BankVerificationStatusPill
                  status={account.verification_status}
                />
              </div>
            </div>
            <dl className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Holder name</dt>
                <dd className="font-medium">{account.account_holder_name}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">IFSC</dt>
                <dd className="font-mono">{account.ifsc_code}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Account number</dt>
                <dd className="font-mono">
                  ••••{account.account_number_last_4}
                </dd>
              </div>
              {account.branch_name !== null && (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Branch</dt>
                  <dd>{account.branch_name}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* An earlier ruling, if any — so a second admin can see who
              already decided and doesn't silently overwrite it. */}
          {hasPriorDecision && (
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs">
              <div className="font-medium">Existing decision</div>
              <div className="mt-1 text-muted-foreground">
                {STATUS_LABEL[account.verification_status]} ·{" "}
                {formatDateTime(account.verified_at)}
                {account.verification_basis !== null && (
                  <> · basis: {account.verification_basis}</>
                )}
              </div>
              {account.verification_notes !== null && (
                <div className="mt-1 italic">
                  “{account.verification_notes}”
                </div>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="bank-verify-basis">Document checked</Label>
            <select
              id="bank-verify-basis"
              value={basis}
              onChange={(e) => {
                setBasis(e.target.value as BankVerificationBasis);
              }}
              disabled={busy}
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {BASIS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {BANK_VERIFICATION_BASIS_LABELS[opt]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="bank-verify-notes">
              Notes {notesRequired ? "(required)" : "(optional)"}
            </Label>
            <Textarea
              id="bank-verify-notes"
              value={notes}
              onChange={(e) => { setNotes(e.target.value); }}
              maxLength={1000}
              disabled={busy}
              placeholder={
                notesRequired
                  ? "Explain what you checked — 'other' has no implied document."
                  : "Visible to admins only"
              }
              aria-required={notesRequired}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              A note is always required to reject.
            </p>
          </div>

          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void submit("rejected")}
              disabled={busy || rejectBlocked}
              title={
                rejectBlocked ? "Add a note explaining the rejection." : undefined
              }
            >
              {submitting === "rejected" ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Rejecting…
                </>
              ) : (
                <>
                  <XCircle className="mr-1 h-4 w-4" /> Reject
                </>
              )}
            </Button>
            <Button
              onClick={() => void submit("verified")}
              disabled={busy || verifyBlocked}
              title={
                verifyBlocked
                  ? "Notes are required when the basis is 'Other'."
                  : undefined
              }
            >
              {submitting === "verified" ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-1 h-4 w-4" /> Mark verified
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
